/**
 * Card view models for the shareholder graph and the phone list.
 *
 * Pure: every string a card shows (share, holds lines, location, aria label) is decided here, so
 * the desktop card, the compact card and the mobile row can never phrase the same node
 * differently. The shares themselves are the backend's (share_in_target, share_in_target_basis,
 * indirect_share_in_target and share_held_by_target on every node); this module only formats them. An
 * older backend that does not send them reads 'n/a', like the figures of a legacy payload.
 */
import { PUNCTUATION } from '../../../../../utils/constants/notation';
import { isNavigableCrefoId } from '../../../../../utils/idUtils';
import { formatOwnershipShare } from '../../../../../utils/ownership';
import {
  GENERAL_PARTNER_SHARE,
  GRAPH_COPY,
  HIDDEN_OWNERS_CAUSE,
  NODE_KIND,
  NOT_APPLICABLE_SHARE,
  SHARE_IN_TARGET_BASIS,
  SHARE_SEPARATOR
} from './graphConstants';
import { isNumericShare } from './graphFilters';

const COMPANY_TYPES = new Set(['company', 'subsidiary']);

const ROLE_LABELS = {
  [NODE_KIND.TARGET]: 'target company',
  [NODE_KIND.OWNER]: 'shareholder',
  [NODE_KIND.SUBSIDIARY]: 'subsidiary',
  [NODE_KIND.AGGREGATE]: 'aggregated shareholders'
};

const SHARE_LABELS = {
  [NODE_KIND.OWNER]: 'Share',
  [NODE_KIND.SUBSIDIARY]: 'Held',
  [NODE_KIND.AGGREGATE]: 'Together'
};

const isBlank = (value) => value == null || String(value).trim() === '';

/** The non-blank parts of a location, trimmed: [city, country], one of them, or none. */
export const locationPartsOf = (city, country) =>
  [city, country].filter((part) => !isBlank(part)).map((part) => String(part).trim());

const joinLocation = (parts) => (parts.length ? parts.join(', ') : null);

/** 'City, Country', a single part, or null. */
export const formatLocation = (city, country) => joinLocation(locationPartsOf(city, country));

/** True when a node names its own place; the target card needs no fallback location then. */
export const hasOwnLocation = (raw) => formatLocation(raw?.city, raw?.country_name) !== null;

/** One share for display: '30%', '>50%', 'GP', or n/a for an unknown value. */
export const formatShare = (share) => formatOwnershipShare(share) ?? PUNCTUATION.EMPTY_VALUE;

/** The chip text of a merged edge, e.g. 'GP · 30%'. */
export const formatEdgeShares = (shares = []) => shares.map(formatShare).join(SHARE_SEPARATOR);

/** True when the node holds its stakes only as general partner (every kept share is 'GP'). */
export const isGeneralPartnerOnly = (outgoingEdges = []) =>
  outgoingEdges.length > 0 &&
  outgoingEdges.every(
    (edge) =>
      edge.shares.length > 0 && edge.shares.every((share) => share === GENERAL_PARTNER_SHARE)
  );

const isUltimateRaw = (raw) => !!(raw?.is_ultimate || raw?.is_beneficiary);

const directEdgeTo = (outgoingEdges, targetId) =>
  outgoingEdges.find((edge) => edge.target === targetId) ?? null;

/** Entity type for the card icon. A company placed as owner never shows the subsidiary icon. */
export const resolveEntityType = (raw, kind) => {
  if (kind === NODE_KIND.TARGET) return 'target';
  if (kind === NODE_KIND.SUBSIDIARY) return 'subsidiary';
  if (kind === NODE_KIND.AGGREGATE || raw?.type === 'entity') return 'entity';
  return raw?.type === 'person' ? 'person' : 'company';
};

/**
 * The share a card shows, as the backend sends it (lib/shareholderGraphShares in td-backend):
 * - owner: share_in_target ('56', 'GP'; n/a when unknown); '–' when share_in_target_basis is
 *   'not_applicable', an owner further up the chain whose share in the target is not shown
 * - subsidiary ('Held'): share_held_by_target, the target's direct stake in it
 * - aggregate ('Together'): the total of the owners the filter hides, the one share added up here
 * - target: none
 * @returns {string|null} formatted, e.g. '56%', 'GP', 'n/a', '–'; null for the target
 */
export const resolveCardShare = (raw, kind, ctx = {}) => {
  if (kind === NODE_KIND.TARGET) return null;
  if (kind === NODE_KIND.AGGREGATE) return formatShare(ctx.aggregate?.totalShare);
  if (kind === NODE_KIND.SUBSIDIARY) return formatShare(raw?.share_held_by_target);
  if (raw?.share_in_target_basis === SHARE_IN_TARGET_BASIS.NOT_APPLICABLE) {
    return NOT_APPLICABLE_SHARE;
  }
  return formatShare(raw?.share_in_target);
};

/** Badges in display order: 'target', 'ultimate', 'generalPartner'. */
export const badgesFor = (raw, kind, outgoingEdges = []) => {
  if (kind === NODE_KIND.TARGET) return ['target'];
  if (kind !== NODE_KIND.OWNER) return [];
  const badges = [];
  if (isUltimateRaw(raw)) badges.push('ultimate');
  if (isGeneralPartnerOnly(outgoingEdges)) badges.push('generalPartner');
  return badges;
};

const numericSharesText = (edge) => {
  const numeric = edge.shares.filter(isNumericShare).map(formatShare);
  return numeric.length ? numeric.join(SHARE_SEPARATOR) : null;
};

const hasGeneralPartnerRole = (edge) => edge.shares.includes(GENERAL_PARTNER_SHARE);

// 'Holds 70% of Muster Holding' / 'General partner of Muster Holding'
const describeHolding = (edge, ownedName) => {
  const shares = numericSharesText(edge);
  if (shares && hasGeneralPartnerRole(edge))
    return `General partner, holds ${shares} of ${ownedName}`;
  if (shares) return `Holds ${shares} of ${ownedName}`;
  if (hasGeneralPartnerRole(edge)) return `General partner of ${ownedName}`;
  return `Holds an unknown share of ${ownedName}`;
};

// 'Holds 80% directly' / 'General partner of the target'
const describeDirectOnly = (edge) => {
  const shares = numericSharesText(edge);
  if (shares) return `Holds ${shares} directly`;
  if (hasGeneralPartnerRole(edge)) return 'General partner of the target';
  return 'Holds an unknown share directly';
};

// The backend's indirect part of an ultimate owner's stake, named only when it comes through a
// single other holding: the split between several holdings is not known.
const viaShareOf = (raw, others) =>
  others.length === 1 ? (raw?.indirect_share_in_target ?? null) : null;

// A holding the target's equity comes through (prepare sets equityToTarget). A general partner
// company that holds only 'GP' passes no equity on, so its holders do not hold 'via' it. Card
// models built without the flag treat every holding as one.
const carriesEquity = (edge) => edge.equityToTarget !== false;

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

// '24% via Muster Holding', or 'via Muster Holding' when the indirect share is not known.
const describeVia = (ownedName, share) => {
  const text = formatOwnershipShare(share);
  return text ? `${text} via ${ownedName}` : `via ${ownedName}`;
};

const describeDirectPart = (edge) => {
  const shares = numericSharesText(edge);
  if (shares) return `${shares} directly`;
  return hasGeneralPartnerRole(edge) ? 'General partner of the target' : 'Unknown share directly';
};

const ownerHoldsLines = (raw, ctx) => {
  const outgoing = ctx.outgoing || [];
  const direct = directEdgeTo(outgoing, ctx.targetId);
  const others = outgoing.filter((edge) => edge !== direct);
  const nameOf = (id) => ctx.nodeById?.get(id)?.member_name ?? PUNCTUATION.EMPTY_VALUE;
  if (direct && !others.length) return [describeDirectOnly(direct)];
  if (direct) {
    const viaShare = viaShareOf(raw, others.filter(carriesEquity));
    const parts = others.map((edge) =>
      carriesEquity(edge)
        ? describeVia(nameOf(edge.target), viaShare)
        : lowerFirst(describeHolding(edge, nameOf(edge.target)))
    );
    return [[describeDirectPart(direct), ...parts].join(', ')];
  }
  return others.map((edge) => describeHolding(edge, nameOf(edge.target)));
};

// Why the aggregated owners are hidden: 'Below the 5% filter', or 'Hidden by the current filters'
// when 'Unknown values: excluded' hides some of them too.
const aggregateCaption = (aggregate, percentageFilter) => {
  if (aggregate?.cause === HIDDEN_OWNERS_CAUSE.FILTERS) return GRAPH_COPY.AGGREGATE_FILTERED;
  const filter = Number.parseFloat(percentageFilter);
  return Number.isNaN(filter)
    ? GRAPH_COPY.AGGREGATE_BELOW_FILTER
    : GRAPH_COPY.AGGREGATE_BELOW_SHARE(filter);
};

/**
 * Human-readable holding lines, as in the mockups:
 * 'Holds 70% of Muster Holding', 'Holds 80% directly', '10% directly, 24% via Muster Holding',
 * '10% directly, holds 50% of Muster Verwaltung' (a holding passing no equity to the target),
 * 'General partner of Muster Holding', '75% held by the target', 'Each below the 5% filter',
 * 'Each hidden by the current filters'.
 */
export const buildHoldsLines = (raw, kind, ctx = {}) => {
  if (kind === NODE_KIND.OWNER) return ownerHoldsLines(raw, ctx);
  if (kind === NODE_KIND.SUBSIDIARY)
    return [`${resolveCardShare(raw, kind, ctx)} held by the target`];
  if (kind === NODE_KIND.AGGREGATE)
    return [`Each ${lowerFirst(aggregateCaption(ctx.aggregate, ctx.percentageFilter))}`];
  return [];
};

/**
 * The lines with stable React keys. Two holdings of same-named companies read identically, so a
 * repeated line gets '#2', '#3' after its text.
 * @param {string[]} [lines]
 * @returns {Array<{key: string, line: string}>}
 */
export const keyedLines = (lines = []) => {
  const seen = new Map();
  return lines.map((line) => {
    const count = (seen.get(line) ?? 0) + 1;
    seen.set(line, count);
    return { key: count === 1 ? line : `${line}#${count}`, line };
  });
};

/**
 * 'Anna Beispiel, shareholder, ultimate owner, share 56%, Hamburg, Germany'; an intermediate
 * owner's '–' share is left out. The aggregate names why its owners are hidden: '2 further
 * shareholders, aggregated shareholders, together 9%, below the 5% filter'.
 */
export const buildAriaLabel = (card, kind) => {
  const parts = [card.name, ROLE_LABELS[kind]];
  if (card.isUltimate) parts.push('ultimate owner');
  if (card.badges.includes('generalPartner')) parts.push('general partner');
  if (card.shareLabel && card.share && card.share !== NOT_APPLICABLE_SHARE) {
    parts.push(`${card.shareLabel.toLowerCase()} ${card.share}`);
  }
  if (card.caption) parts.push(lowerFirst(card.caption));
  if (card.location) parts.push(card.location);
  return parts.filter(Boolean).join(', ');
};

/**
 * Id to open the node's company page with: the live profile uuid when known (graph view figures
 * or the node's own company_uuid), else the crefo member_id. Persons always use member_id.
 */
export const resolveNodeLinkId = (raw, figures) => {
  if (raw?.type === 'person') return raw.member_id ?? null;
  return figures?.companyUuid || raw?.company_uuid || raw?.member_id || null;
};

/**
 * Check if a graph node can be navigated to: a person with a numeric crefo id, or a company
 * (subsidiary) with a numeric crefo id or a live profile uuid. The one gate of the graph cards,
 * the table rows and the new-tab handler.
 * @param {Object} node - Graph node object
 * @param {string} [companyUuid] - the live profile uuid when known outside the node (graph view
 *   figures); the node's own company_uuid counts too
 * @returns {boolean} Whether the node can be opened in a detail page
 */
export const canNavigateToNodeDetails = (node, companyUuid) => {
  if (node?.target) return false;
  if (node?.type === 'person') return isNavigableCrefoId(node.member_id);
  if (!COMPANY_TYPES.has(node?.type)) return false;
  return isNavigableCrefoId(node.member_id) || !!(companyUuid || node.company_uuid);
};

const isNavigable = (raw, kind, figures) =>
  kind !== NODE_KIND.TARGET &&
  kind !== NODE_KIND.AGGREGATE &&
  canNavigateToNodeDetails(raw, figures?.companyUuid);

const resolveLocationParts = (raw, kind, targetLocation) => {
  if (hasOwnLocation(raw) || kind !== NODE_KIND.TARGET) {
    return locationPartsOf(raw?.city, raw?.country_name);
  }
  return locationPartsOf(targetLocation?.city, targetLocation?.country);
};

const lookupFigures = (raw, entityType, figuresByMemberId) => {
  if (entityType === 'person' || !figuresByMemberId?.get) return null;
  return figuresByMemberId.get(String(raw?.member_id)) ?? null;
};

/**
 * @param {Object} raw - the reclassified node copy (legacy shape)
 * @param {Object} ctx
 * @param {'target'|'owner'|'subsidiary'} ctx.kind
 * @param {string} ctx.targetId
 * @param {Array} [ctx.outgoing] - kept edges this node owns (owner -> owned), before collapse;
 *   equityToTarget: false on a holding no equity reaches the target through
 * @param {Array} [ctx.roleEdges] - every stake the node holds, unfiltered: the general partner
 *   badge follows these, so a share filter never changes it (default: ctx.outgoing)
 * @param {Map} [ctx.nodeById] - kept node copies by id
 * @param {Map} [ctx.figuresByMemberId] - NodeFigures by String(member_id)
 * @param {{city, country}} [ctx.targetLocation] - fallback location for the target card
 * @returns {Object} CardModel
 */
export const buildCardModel = (raw, ctx) => {
  const { kind } = ctx;
  const entityType = resolveEntityType(raw, kind);
  const figures = lookupFigures(raw, entityType, ctx.figuresByMemberId);
  const locationParts = resolveLocationParts(raw, kind, ctx.targetLocation);
  const card = {
    name: raw?.member_name ?? PUNCTUATION.EMPTY_VALUE,
    entityType,
    location: joinLocation(locationParts),
    // [city, country] for the tables, which lay them out on two lines (LocationText)
    locationParts,
    badges: badgesFor(raw, kind, ctx.roleEdges ?? ctx.outgoing),
    shareLabel: SHARE_LABELS[kind] ?? null,
    share: resolveCardShare(raw, kind, ctx),
    // What an owner's share is (share_in_target_basis); the chip colour follows it.
    shareBasis: kind === NODE_KIND.OWNER ? (raw?.share_in_target_basis ?? null) : null,
    isUltimate: kind === NODE_KIND.OWNER && isUltimateRaw(raw),
    birthYear: entityType === 'person' ? (raw?.birth_year ?? null) : null,
    figures,
    holdsLines: buildHoldsLines(raw, kind, ctx),
    navigable: isNavigable(raw, kind, figures),
    linkId:
      kind === NODE_KIND.OWNER || kind === NODE_KIND.SUBSIDIARY
        ? resolveNodeLinkId(raw, figures)
        : null,
    companyUuid: figures?.companyUuid || raw?.company_uuid || null
  };
  card.ariaLabel = buildAriaLabel(card, kind);
  return card;
};

/**
 * Card for the 'N further shareholders' node.
 * @param {{count: number, totalShare: string|null, cause?: string}} aggregate - cause: a
 *   HIDDEN_OWNERS_CAUSE, the share filter when left out
 * @param {{percentageFilter: number}} ctx
 */
export const buildAggregateCardModel = (aggregate, ctx = {}) => {
  const kind = NODE_KIND.AGGREGATE;
  const card = {
    name: `${aggregate.count} further shareholder${aggregate.count === 1 ? '' : 's'}`,
    caption: aggregateCaption(aggregate, ctx.percentageFilter),
    entityType: 'entity',
    location: null,
    locationParts: [],
    badges: [],
    shareLabel: SHARE_LABELS[kind],
    share: resolveCardShare(null, kind, { aggregate }),
    shareBasis: null,
    isUltimate: false,
    birthYear: null,
    figures: null,
    holdsLines: buildHoldsLines(null, kind, { ...ctx, aggregate }),
    navigable: false,
    linkId: null,
    companyUuid: null,
    count: aggregate.count
  };
  card.ariaLabel = buildAriaLabel(card, kind);
  return card;
};
